// Página "Visuales". Orquesta: carga la ESTRUCTURA del tablero desde la BD
// (visual_dashboards/visual_widgets, vía getDefaultVisualDashboard), pide los
// DATOS numéricos al backend (Athena, /api/visuals/pricing-analytics), y
// delega el render a VisualRenderer (lectura) o a VisualBuilder (edición admin).
// La presentación vive en la config (ahora en BD) + los widgets reutilizables.
import React, { useEffect, useState, useCallback } from 'react';
import { useAuthenticator } from '@aws-amplify/ui-react';
import VisualRenderer from '../../visuals/VisualRenderer';
import VisualBuilder from '../../visuals/VisualBuilder';
import FilterBar from '../../visuals/base/FilterBar';
import { getDefaultVisualDashboard } from '../../services/visualsBuilderService';
import { mapDashboardToConfig } from '../../visuals/configs/mapDashboardToConfig';
import './VisualsPage.css';

const STRANDS_API_URL = process.env.REACT_APP_STRANDS_API_URL || 'http://localhost:5001';

// Gate del builder admin. POC: lista simple; reemplazar por rol/claim real de Cognito.
const ADMIN_USERS = new Set(['admin-main-demo']);

export default function VisualsPage() {
  const { user } = useAuthenticator((context) => [context.user]);
  const username = user?.username;
  const isAdmin = !!username && ADMIN_USERS.has(username);

  const [config, setConfig] = useState(null);     // estructura del tablero (de BD)
  const [structLoading, setStructLoading] = useState(true);
  const [data, setData] = useState(null);         // payload numérico (de Athena)
  const [filterOptions, setFilterOptions] = useState({});
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(false);

  const theme = config?.theme || {};

  // 1) Estructura del tablero desde la BD.
  useEffect(() => {
    if (!username) return;
    let alive = true;
    setStructLoading(true);
    getDefaultVisualDashboard(username)
      .then((res) => {
        if (!alive) return;
        setConfig(mapDashboardToConfig(res.dashboard));
      })
      .catch(() => { if (alive) setError('No se pudo cargar la estructura del tablero'); })
      .finally(() => { if (alive) setStructLoading(false); });
    return () => { alive = false; };
  }, [username]);

  // 2) Datos numéricos: depende de la config (dataset/engine/dateCutoff) + filtros.
  const loadData = useCallback(async () => {
    if (!username || !config) return;
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams({ username, dataset: config.dataset });
      if (config.engine) params.append('engine', config.engine);
      if (config.dateCutoff) params.append('cutoff', config.dateCutoff);
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });

      const resp = await fetch(`${STRANDS_API_URL}/api/visuals/pricing-analytics?${params}`);
      const json = await resp.json();
      if (json.success) {
        setData(json.data);
        if (json.data?.filters) setFilterOptions(json.data.filters);
      } else {
        setError(json.error || 'Error al cargar datos');
      }
    } catch (e) {
      setError('Error de conexión con el servidor');
    } finally {
      setLoading(false);
    }
  }, [username, config, filters]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  if (structLoading && !config) {
    return (
      <div className="vz-page">
        <div className="vz-loading">Cargando tablero…</div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="vz-page">
        {error ? (
          <div className="vz-card vz-error" style={{ margin: 24 }}>
            {error}. Revisa que el backend con las rutas <code>/api/visuals/*</code> esté levantado
            (apuntando a <code>{STRANDS_API_URL}</code>).
          </div>
        ) : (
          <div className="vz-card vz-empty" style={{ margin: 24 }}>
            No hay un dashboard de Visuales configurado para este usuario.
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="vz-page" style={{ background: theme.pageBg }}>
      <div className="vz-header" style={{ background: theme.headerBg, color: theme.headerText }}>
        <div className="vz-header-title">{config.title}</div>
        <div className="vz-header-meta">
          {data?.meta?.lastUpdated && <span>Actualizado: {data.meta.lastUpdated}</span>}
          {isAdmin && (
            <button
              className={`vz-btn-edit ${editMode ? 'active' : ''}`}
              onClick={() => setEditMode((v) => !v)}
            >
              {editMode ? '✓ Listo' : '✎ Editar'}
            </button>
          )}
          <button className="vz-refresh" onClick={loadData} disabled={loading}>
            {loading ? 'Cargando…' : '↻ Actualizar'}
          </button>
        </div>
      </div>

      {!editMode && (
        <FilterBar
          filters={config.filters}
          options={filterOptions}
          value={filters}
          onChange={handleFilterChange}
          theme={theme}
        />
      )}

      {error && <div className="vz-card vz-error">{error}</div>}

      {editMode && isAdmin ? (
        <VisualBuilder username={username} data={data || {}} />
      ) : loading && !data ? (
        <div className="vz-loading">Cargando dashboard…</div>
      ) : (
        <VisualRenderer config={config} data={data || {}} />
      )}
    </div>
  );
}
